import { Cancel, Close, Delete, Edit, Launch, PlayArrow, Refresh, Stop } from "@mui/icons-material";
import { Box, Button, CircularProgress, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormLabel, IconButton, InputAdornment, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../context/useSocket";
import { addCDGRJob, deleteCDGRJob, getCDGRJobs, selectCDGRJobs } from "../reducers/cdgrJobSlice";
import { selectUser } from "../reducers/userSlice";
import { ViewCDGRJob } from "./ViewCDGRJob";


export const ManageCDGRJobs = ({ darkMode, setManageJobsVisible }) => {
  
  const theme = useTheme();
  const dispatch = useDispatch();


  const { socket } = useSocket();
  const user = useSelector(selectUser);
  const demoUser = user?.value?.subscription === 'Demo';
  const cdgrJobs = useSelector(selectCDGRJobs);

  const [jobName, setJobName] = useState('Evergreen Master Job');
  const [jobDescription, setJobDescription] = useState('Default Evergreen job for all wells');
  const [jobFrequency, setJobFrequency] = useState('off');
  const [groupWellName, setGroupWellName] = useState('');
  const [wellErrorThreshold, setWellErrorThreshold] = useState(10);
  const [typeCurveErrorThreshold, setTypeCurveErrorThreshold] = useState(10);
  const [isJobRunning, setIsJobRunning] = useState(false);
  const [viewCDGRJobVisible, setViewCDGRJobVisible] = useState(false);
  const [isLoadingCDGRJobData, setIsLoadingCDGRJobData] = useState(false);
  const [isLoadingCDGRJobs, setIsLoadingCDGRJobs] = useState(false);
  const [editCDGRJobVisible, setEditCDGRJobVisible] = useState(false);
  const [deleteCDGRJobVisible, setDeleteCDGRJobVisible] = useState(false);
  const [cdgrJobDataProgress, setCDGRJobDataProgress] = useState(0);

  const onMessage = useCallback((message) => {
    const response = JSON.parse(message?.data);

    switch(response?.messageType) {
      case 'cdgrCurrentEvergreenJob':
        break;
        
      case 'cdgrCurrentEvergreenJobCompleted':
        dispatch(addCDGRJob(response?.payload));
        setIsJobRunning(false);
        break;

      default:
        break;
    };

  }, [dispatch]);


  const useGetJobs = () => {
    useEffect(() => {
      let active = true;
  
      if (active) {
        getJobs();
      };
  
      return () => active = false;
  
    }, []);
  };

  useGetJobs(cdgrJobs);


  useEffect(() => {
    socket.addEventListener("message", onMessage);

    return () => {
      socket.removeEventListener("message", onMessage);
    };
  }, [socket, onMessage]);

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 250,
      editable: false,
    },
    {
      field: 'completed',
      headerName: 'Completed',
      width: 200,
      editable: false,
    },
    {
      field: 'total',
      headerName: 'Total Wells',
      width: 75,
      editable: false,
    },
    {
      field: 'wells',
      headerName: 'Processed',
      width: 75,
      editable: false,
    },
    {
      field: 'qcFlags',
      headerName: 'QC Flags',
      width: 75,
      editable: false,
    },
    {
      field: 'updated',
      headerName: 'Updated',
      width: 75,
      editable: false,
    },
    {
      field: 'recentShutIns',
      headerName: 'Recent Shut-Ins',
      width: 75,
      editable: false,
    },
    {
      field: 'nonProducing',
      headerName: 'Non-producing',
      width: 75,
      editable: false,
    },
    {
      field: 'typeCurves',
      headerName: 'Type Curves',
      width: 75,
      editable: false,
    },
    {
      field: 'wellErrorThreshold',
      headerName: 'Well Error Threshold',
      width: 75,
      editable: false,
    },
    {
      field: 'typeCurveErrorThreshold',
      headerName: 'TC Error Threshold',
      width: 75,
      editable: false,
    },
    {
      field: 'delete',
      headerName: 'Delete',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          // style={{ marginLeft: 16 }}
          startIcon={<Delete />}
          onClick={(e) => {
            setDeleteCDGRJobVisible(params?.id);
          }}
          disabled={(!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
        >
          Delete Job
        </Button>
      ),
    },
    {
      field: 'load',
      headerName: 'Load',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          // style={{ marginLeft: 16 }}
          startIcon={<Launch />}
          onClick={(e) => {
            const job = cdgrJobs?.byId?.[params?.id];
            loadJob(job?.id, job?.location);
          }}
        >
          Load Job
        </Button>
      ),
    },
  ];

  return (
    <Dialog 
      open={true} 
      onClose={() => setManageJobsVisible(false)}
      fullWidth={true}
      maxWidth="xl"
      PaperProps={{
        style: {
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        },
      }}
      elevation={0}
    >
      <DialogTitle>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography variant="h6">
            Manage Jobs
          </Typography>
          <IconButton onClick={() => setManageJobsVisible(false)}><Close /></IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{overflow: 'hidden'}}>

        <div>
          <Typography variant="h6" style={{marginBottom: 0}}>Scheduled job</Typography>
          <Paper style={{padding: 10, boxShadow: isJobRunning && `inset 0px 0px 20px rgba(7, 88, 183, 0.75)`}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>

              <div>
                <Typography fontSize={10}>NAME</Typography>
                <Typography>{jobName}</Typography>
                {/* <TextField value={jobName} onChange={e => setJobName(e.target.value)}></TextField> */}
              </div>
              <div style={{marginLeft: 40}}>
                <Typography fontSize={10}>STATUS</Typography>
                <Typography>{isJobRunning ? 'Running...' : socket?.readyState === 1 ? 'Ready' : 'Disconnected'}</Typography>
              </div>
              <div style={{marginLeft: 40}}>
                <Typography fontSize={10}>NEXT SCHEDULED RUN</Typography>
                <Typography>No repeat</Typography>
              </div>
              <div style={{marginLeft: 40}}>
                <Typography fontSize={10}>JOB FREQUENCY</Typography>
                <Typography>{jobFrequency}</Typography>
              </div>
              <div style={{marginLeft: 40}}>
                <Typography fontSize={10}>WELL ERROR TOLERANCE</Typography>
                <Typography>{wellErrorThreshold}%</Typography>
              </div>
              <div style={{marginLeft: 40}}>
                <Typography fontSize={10}>TC ERROR TOLERANCE</Typography>
                <Typography>{typeCurveErrorThreshold}%</Typography>
              </div>
              
              <div style={{display: 'flex', marginTop: 0, alignItems: 'center'}}>

                <Button
                  startIcon={<Edit />}
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => setEditCDGRJobVisible(true)}
                  disabled={isJobRunning || (!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
                >
                  Edit Job
                </Button>

                <Button
                  startIcon={isJobRunning ? <Stop /> : <PlayArrow />}
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{marginLeft: 10}}
                  onClick={() => handleStartStopEvergreen(
                    socket, 
                    jobName,
                    jobDescription, 
                    jobFrequency,
                    groupWellName, 
                    wellErrorThreshold,
                    typeCurveErrorThreshold, 
                    user?.value?.email, 
                    isJobRunning, 
                    setIsJobRunning
                  )}
                  disabled={socket?.readyState !== 1 || (!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
                >
                  {isJobRunning ? "Stop Job" : "Start Job"}
                </Button>
              </div>
            </div>
          </Paper>
          
        </div>

        <div style={{marginTop: 40}}>
          <div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 5}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <Typography variant="h6">Completed jobs</Typography>
              <Typography fontSize={12} style={{marginTop: 5, marginLeft: 10}}>{cdgrJobs?.allIds?.length || 0} TOTAL</Typography>
            </div>
            <Button
              // variant="outlined"
              color="primary"
              onClick={getJobs} 
              startIcon={<Refresh />}
              size="small"
            >
              Refresh
            </Button>
          </div>
          <Divider />

          <div style={{minHeight: 400, maxHeight: 500, overflow: 'scroll'}}>
            <DataGrid
              rows={getRows()}
              columns={columns}
              rowsPerPageOptions={[10,25,50,100]}
              autoHeight={true}
              disableSelectionOnClick
              style={{height: '100%'}}
            />
          </div>

        </div>

        {viewCDGRJobVisible && <ViewCDGRJob setViewCDGRJobVisible={setViewCDGRJobVisible} setManageJobsVisible={setManageJobsVisible} />}

        {isLoadingCDGRJobs && 
        <div style={{backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)', position: 'absolute', left: 0, top: 0, zIndex: 420, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}>
          <CircularProgress></CircularProgress>
          <Typography style={{marginTop: 10, fontWeight: 'bold', fontSize: 20}}>Loading jobs...</Typography>
          <IconButton style={{position: 'absolute', bottom: '60px'}} onClick={() => setIsLoadingCDGRJobs(false)}><Cancel color="grey" /> </IconButton>
        </div>}

        {isLoadingCDGRJobData && 
        <div style={{backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', position: 'absolute', left: 0, top: 0, zIndex: 420, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={cdgrJobDataProgress} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                component="div"
                // color="text.secondary"
              >{`${Math.round(cdgrJobDataProgress)}%`}</Typography>
            </Box>
          </Box>
          <Typography style={{marginTop: 10, fontWeight: 'bold', fontSize: 20}}>Loading job...</Typography>
          <IconButton style={{position: 'absolute', bottom: '60px'}} onClick={() => setIsLoadingCDGRJobData(false)}><Cancel color="grey" /> </IconButton>
        </div>}

      </DialogContent>


      <DialogActions style={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button 
          onClick={() => setManageJobsVisible(false)} 
          color={darkMode ? "inherit" : "primary"} 
          style={{marginRight: 10}}
        >
          Cancel
        </Button>
        {/* <Button 
          onClick={() => {}} 
          color={darkMode ? "inherit" : "primary"}
          style={{color: darkMode ? '#000' : '#fff'}} 
          variant="contained"
        >
          Update
        </Button> */}
      </DialogActions>

      {editCDGRJobVisible && <EditDialog
        cdgrJobId={deleteCDGRJobVisible}
        setEditCDGRJobVisible={setEditCDGRJobVisible}
        jobName={jobName}
        setJobName={setJobName}
        jobDescription={jobDescription}
        setJobDescription={setJobDescription}
        jobFrequency={jobFrequency}
        setJobFrequency={setJobFrequency}
        groupWellName={groupWellName}
        setGroupWellName={setGroupWellName}
        darkMode={darkMode}
        wellErrorThreshold={wellErrorThreshold}
        setWellErrorThreshold={setWellErrorThreshold}
        typeCurveErrorThreshold={typeCurveErrorThreshold}
        setTypeCurveErrorThreshold={setTypeCurveErrorThreshold}
      />}
      {deleteCDGRJobVisible && <DeleteDialog cdgrJobId={deleteCDGRJobVisible} cdgrJobName={cdgrJobs?.byId?.[deleteCDGRJobVisible]?.name} />}
    </Dialog>
  );


  function getRows() {
    try {
      let rows = [];
  
      if (!cdgrJobs?.allIds?.length > 0) return [];

      [...cdgrJobs?.allIds].reverse()?.forEach(id => {
        const cdgrJob = cdgrJobs?.byId?.[id];

        if (cdgrJob && ((!demoUser && cdgrJob?.name !== 'Demo Job') || (demoUser && cdgrJob?.name === 'Demo Job'))) {
          rows.push({
            id: id || Math.random(),
            name: cdgrJob?.name,
            completed: new Date(cdgrJob?.completed)?.toLocaleString() || "",
            total: Math.max((cdgrJob?.totalWells || 0), cdgrJob?.wells?.length),
            wells: cdgrJob?.wells?.length || 0,
            qcFlags: cdgrJob?.qcFlags?.length || 0,
            updated: (cdgrJob?.wells?.length - cdgrJob?.qcFlags?.length) || 0,
            recentShutIns: cdgrJob?.recentShutIns?.length || 0,
            nonProducing: cdgrJob?.nonProducing?.length || 0,
            typeCurves: cdgrJob?.typeCurves?.length || 0,
            wellErrorThreshold: `${cdgrJob?.wellErrorThreshold || 0}%`,
            typeCurveErrorThreshold: `${cdgrJob?.typeCurveErrorThreshold || 0}%`,
            load: id,
            delete: id
          });
        };
      });
      return rows;
    } catch (error) {
      console.log(error);
      return [];
    };
  };

  // function handleJobClick(e, jobId) {
  //   e.stopPropagation();
  //   setCurrentJobId(jobId);
  // };

  

  function getJobs() {
    console.log('Getting CDGR jobs...');
    setIsLoadingCDGRJobs(true);
    dispatch(getCDGRJobs())
    .then(() => setIsLoadingCDGRJobs(false));
  };

  function deleteJob(cdgrJobId) {
    if (!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL)) return;

    dispatch(deleteCDGRJob(cdgrJobId))
    .then(() => setDeleteCDGRJobVisible(false));
  };

  // function loadJob(jobLocation) {
  //   setIsLoadingCDGRJobData(true);
  //   dispatch(loadCDGRJob(jobLocation)).then(() => {
  //     setViewCDGRJobVisible(true);
  //     setIsLoadingCDGRJobData(false);
  //   })
  // };

  function loadJob(jobId, jobLocation) {
    try {
      if (isLoadingCDGRJobData) return;

      if (!jobLocation) {
        setViewCDGRJobVisible(true);
        return;
      };

      setIsLoadingCDGRJobData(true);
      axios
      .get(jobLocation, {
        'Cache-Control': 'no-cache',
        'cache': 'no-store',
        onDownloadProgress: progressEvent => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setCDGRJobDataProgress(percentage);
          // if (percentage === 100) {
          //   setTimeout(() => {
          //     setIsLoadingCDGRJobData(false);
          //   }, 400);
          // }
        }
      })
      .then(res => {
        dispatch(addCDGRJob(res.data));
        setViewCDGRJobVisible(true);
        setIsLoadingCDGRJobData(false);
      })
      .catch(err => {
        console.log(err);
        setViewCDGRJobVisible(true);
        setIsLoadingCDGRJobData(false);
        return;
      });
    } catch (error) {
      console.log(error);
      setViewCDGRJobVisible(true);
      setIsLoadingCDGRJobData(false);
      return;
    };

    // console.log(cdgrJobs?.byId?.[jobId]);

   
  };

  function DeleteDialog({ cdgrJobId, cdgrJobName }) {
    return (
      <ClickAwayListener onClickAway={() => setDeleteCDGRJobVisible(false)}>
        <Dialog open>
          <DialogTitle>
            <Typography>Delete Job?</Typography>
          </DialogTitle>
          <DialogContent>
            {`You are about to delete job ${cdgrJobName}. This action cannot be undone. Do you wish to continue?`}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteCDGRJobVisible(false)} color="primary">Cancel</Button>
            <Button onClick={() => deleteJob(cdgrJobId)} color="secondary" disabled={!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL)}>Delete</Button>
          </DialogActions>
        </Dialog>
      </ClickAwayListener>
    );
  };
};

function EditDialog({ 
  setEditCDGRJobVisible,
  jobName,
  setJobName,
  jobDescription,
  setJobDescription,
  jobFrequency,
  setJobFrequency,
  groupWellName,
  setGroupWellName,
  darkMode,
  wellErrorThreshold,
  setWellErrorThreshold,
  typeCurveErrorThreshold,
  setTypeCurveErrorThreshold
}) {
  return (
    <ClickAwayListener onClickAway={() => setEditCDGRJobVisible(false)}>
      <Dialog 
        open
        fullWidth={true}
        maxWidth="sm"
        onClose={() => setEditCDGRJobVisible(false)}
      >
        <DialogTitle>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Typography variant="h6">
              Edit Job
            </Typography>
            <IconButton onClick={() => setEditCDGRJobVisible(false)}><Close /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{display: 'flex', flexDirection: 'column', }}>

          <TextField
            label="Job Name"
            size="small"
            value={jobName}
            onChange={e => setJobName(e.target.value)}
            style={{marginTop: 10, marginBottom: 20, width: '100%'}}
          />

          <TextField
            label="Job Description"
            size="small"
            value={jobDescription}
            onChange={e => setJobDescription(e.target.value)}
            style={{marginTop: 10, marginBottom: 20, width: '100%'}}
          />

          <TextField
            label="Group Well Name"
            size="small"
            value={groupWellName}
            onChange={e => setGroupWellName(e.target.value)}
            style={{marginTop: 10, marginBottom: 20, width: '100%'}}
            disabled
          />

          <FormControl style={{marginRight: 10, marginBottom: 10}}>
            <FormLabel component="legend">Job Repeat Frequency</FormLabel>

            <ToggleButtonGroup
              value={jobFrequency}
              onChange={(e, newValue) => newValue && setJobFrequency(newValue)}
              size="small"
              style={{height: 30, marginTop: 5}}
              exclusive
            >
              <ToggleButton value="weekly" fontSize="small" color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Weekly
                </Typography>
              </ToggleButton>
              <ToggleButton value="2 weeks" fontSize="small" color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  2 Weeks
                </Typography>
              </ToggleButton>
              <ToggleButton value="monthly" fontSize="small" color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Monthly
                </Typography>
              </ToggleButton>
              <ToggleButton value="off" fontSize="small" color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Off
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <div style={{display: 'flex', marginTop: 20, marginBottom: 20}}>
            <TextField
              label="Well Error Tolerance"
              size="small"
              value={wellErrorThreshold}
              onChange={e => setWellErrorThreshold(Number(e.target.value))}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
              style={{width: 150, marginRight: 20}}
            />
            <TextField
              label="TC Error Tolerance"
              size="small"
              value={typeCurveErrorThreshold}
              onChange={e => setTypeCurveErrorThreshold(Number(e.target.value))}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
              style={{width: 150}}
            />
          </div>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCDGRJobVisible(false)} color="primary">Cancel</Button>
          <Button onClick={() => setEditCDGRJobVisible(false)} color="secondary">Save</Button>
        </DialogActions>
      </Dialog>
    </ClickAwayListener>
  );
};

function handleStartStopEvergreen(socket, jobName,
  jobDescription, jobFrequency, groupWellName, wellErrorThreshold,
  typeCurveErrorThreshold, userEmail, isJobRunning, setIsJobRunning) {
    if (!userEmail?.includes('ssdynamics.com') && !userEmail?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL)) return;

  
    if (socket && socket.readyState === 1) {
    if (isJobRunning) {
      console.log('Sending CDGR evergreen stop request...');

      setIsJobRunning(false);
      
      socket?.send(JSON.stringify({
        messageType: 'cdgrEvergreenStop'
        // evergreenJobKey
      }));
      
    } else {
      console.log('Sending CDGR evergreen start request...');
      setIsJobRunning(true);
      socket?.send(JSON.stringify({
        messageType: 'cdgrEvergreenStart',
        jobName,
        jobDescription,
        jobFrequency,
        groupWellName,
        wellErrorThreshold,
        typeCurveErrorThreshold,
        userEmail
      }));
    };
  };
};
